import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state';

export const VARIABLE_HYDRATION_PLUGIN_KEY = new PluginKey('variableHydration');

// `survey.link` is owned by SurveyNode / SurveyLinkReplacement — never let the
// variable hydration claim it.
const RESERVED_VARIABLES = new Set(['survey.link']);

// Matches a complete `{{ name }}` token (no nested braces). Trimmed name is
// captured. Only fires once the closing `}}` exists, which is what makes
// validation happen exactly when the user finishes typing the brackets.
const VARIABLE_REGEX = /\{\{\s*([^{}]+?)\s*\}\}/g;

/**
 * Converts supported `{{name}}` text into atom `variable` nodes (see
 * VariableNode), generalized from the survey `SurveyLinkReplacement` extension.
 *
 * Runs on every doc change AND whenever the supported-variable list updates, so
 * it uniformly covers: loading saved templates (setContent), replaceContent,
 * paste, manual typing, and picker insertion. Unsupported tokens are left as
 * plain text for `variable-highlight-decoration` to render (red + ✕).
 */
export const VariableHydration = Extension.create({
  name: 'variableHydration',

  addOptions() {
    return {
      supportedVariables: [],
    };
  },

  addCommands() {
    return {
      // Push a new supported-variable list at runtime. Returning the meta
      // transaction lets `appendTransaction` re-hydrate against the new list.
      setHydrationVariables:
        (variables) =>
        ({ state, dispatch }) => {
          if (dispatch) {
            dispatch(
              state.tr.setMeta(VARIABLE_HYDRATION_PLUGIN_KEY, variables || [])
            );
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const initialSupported = this.options.supportedVariables || [];

    return [
      new Plugin({
        key: VARIABLE_HYDRATION_PLUGIN_KEY,

        state: {
          init() {
            return { supportedVariables: initialSupported };
          },
          apply(tr, pluginState) {
            const updated = tr.getMeta(VARIABLE_HYDRATION_PLUGIN_KEY);
            if (updated) {
              return { supportedVariables: updated };
            }
            return pluginState;
          },
        },

        appendTransaction(transactions, _oldState, newState) {
          const listUpdated = transactions.some((tr) =>
            tr.getMeta(VARIABLE_HYDRATION_PLUGIN_KEY)
          );
          const docChanged = transactions.some((tr) => tr.docChanged);
          if (!docChanged && !listUpdated) return null;

          const { supportedVariables = [] } =
            VARIABLE_HYDRATION_PLUGIN_KEY.getState(newState) || {};
          if (!supportedVariables.length) return null;
          const supported = new Set(supportedVariables);

          const { doc, selection } = newState;

          // Collect every convertible match first, then apply them from the
          // end of the document backwards so earlier positions stay valid as
          // text is replaced by (shorter) atom nodes.
          const matches = [];
          doc.descendants((node, pos) => {
            if (!node.isText) return;
            const text = node.text || '';
            VARIABLE_REGEX.lastIndex = 0;
            let match;
            while ((match = VARIABLE_REGEX.exec(text)) !== null) {
              const name = match[1].trim();
              if (RESERVED_VARIABLES.has(name) || !supported.has(name))
                continue;

              const from = pos + match.index;
              const to = from + match[0].length;

              // Whether the caret is the one editing this token — used below to
              // push the selection past the new pill so editing can't continue
              // (e.g. fixing an invalid token into a valid one in place).
              const caretInside = selection.from > from && selection.to < to;

              matches.push({ from, to, name, caretInside });
            }
          });

          if (!matches.length) return null;

          const { tr } = newState;
          // Original `from` of the token the caret was editing (at most one).
          let caretTokenFrom = null;
          matches
            .sort((a, b) => b.from - a.from)
            .forEach(({ from, to, name, caretInside }) => {
              tr.replaceWith(
                from,
                to,
                newState.schema.nodes.variable.create({ id: name })
              );
              if (caretInside) caretTokenFrom = from;
            });

          // Drop the caret just after the pill it was inside (map the original
          // start through every replacement; the atom node has size 1).
          if (caretTokenFrom !== null) {
            const pos = Math.min(
              tr.mapping.map(caretTokenFrom) + 1,
              tr.doc.content.size
            );
            tr.setSelection(Selection.near(tr.doc.resolve(pos)));
          }

          return tr;
        },
      }),
    ];
  },
});

export default VariableHydration;
